// The hand: what the cards are, and where each one sits in the fan (PLAN-UI
// §28.3, U7; screen-mock `#hand`).
//
// **Pure, and that is the point.** The fan is arithmetic — a spread, a swing,
// a lift and a stacking order — and arithmetic drawn straight into JSX is
// arithmetic nothing can assert about. `HandFan.tsx` is the DOM; this is the
// answer it draws, so the rule the layout depends on (a hover only ever grows
// the seat the pointer is already in, or the card jumps out from under the
// cursor and Playwright clicks the wrong one) is a test rather than a
// screenshot.
//
// No React and no DOM here. **What the cards are moved** (PLAN-CLIENT-SPLIT
// day 6): `handModel`, the glyphs and the loose-artifact sentence are
// `src/engine/session/hand.ts`; this is the fan alone and reads no rule.


// --- the fan ----------------------------------------------------------------

/** The kit `GameCard`'s own size. The fan is laid out in these units. */
export const CARD_W = 138;
export const CARD_H = 196;

/**
 * How far a card's foot sits below the bottom of the screen at rest.
 *
 * The mock buries 64 of a 168px card (`#hand { bottom:-64px }`), so the hand
 * reads as something held rather than as a row of panels; scaled to the kit's
 * card that is 74. **The centre has to stay on screen**: Playwright clicks an
 * element's centre and its hit test runs before the pointer arrives, so a card
 * whose middle is under the viewport edge is a card no smoke can play.
 */
export const CARD_SUNK = 74;

/** What the fan occupies on screen at rest — the registered inset's depth. */
export const FAN_HEIGHT = CARD_H - CARD_SUNK;

/** `GameCard`'s `transform-origin: 50% 120%`, as a share of the height. */
const PIVOT = 1.2;

/** Distance from the pivot (`50% 120%`) to a card's centre. */
const FAN_RADIUS = CARD_H * PIVOT - CARD_H / 2;

/**
 * How wide the resting fan is allowed to get, and how tight it may pack.
 *
 * **Spacing compresses with the count, and overlap is the point.** The first
 * draft stepped a flat 72px so that no card's centre was ever under its
 * neighbour — Playwright's constraint, written into the layout — and twenty
 * cards were then 1.4 metres of hand running off both edges of the screen.
 * The seats do that job now (`fanSeats`), so the drawing is free to pack: the
 * centres share `FAN_MAX_WIDTH` between them, down to `MIN_SPREAD` before the
 * fan starts growing again.
 */
const FAN_MAX_WIDTH = 620;
const MIN_SPREAD = 14;
const MAX_SPREAD = 40;

/** Degrees between neighbours, and the total swing they are allowed. */
const STEP_DEG = 7;
const SPAN_DEG = 28;

/** How far the card beside the one being read steps aside. Halves per step. */
const PART = 22;

/** The whole hand rises when the pointer is in it — the mock's `bottom:52px`. */
const HAND_LIFT = 52;

/**
 * And the one under the pointer rises to stand on the screen edge.
 *
 * `CARD_SUNK` exactly: a card is buried by that much at rest, so lifting it by
 * the same puts its foot on the bottom of the screen and the *whole* card —
 * rarity glow included — on it. Higher was the mock's 96 and cost the hover
 * card the room it needed above.
 */
const CARD_LIFT = CARD_SUNK;

/** How much bigger the hovered card gets. */
const HOVER_SCALE = 1.08;

/**
 * How far above its resting top edge a card can ever get.
 *
 * **Measured from the pivot, not from the middle.** `transform-origin: 50%
 * 120%` sits `1.2·CARD_H` below the top edge, so scaling by `s` raises that
 * edge by `(s − 1)·1.2·CARD_H` — 18.8px, not the 7.8px an origin-at-the-middle
 * reading gives. That was the whole of why the hover card landed flush on the
 * raised card's name plate (designer, U7 review).
 */
export const CARD_REACH = Math.ceil(CARD_LIFT + (HOVER_SCALE - 1) * PIVOT * CARD_H);

/** Clear air between the top of a raised card and the card explaining it. */
const HOVER_GAP = 14;

/**
 * The highest point any card can reach above the bottom edge of the screen —
 * what the refusal pill stands clear of, whatever the hand is doing.
 */
export const HAND_REACH = FAN_HEIGHT + CARD_REACH;

/** The anchor box per card: constant, and taller than the card ever is. */
export const PERCH_W = Math.ceil(HOVER_SCALE * CARD_W);
export const PERCH_H = CARD_H + CARD_REACH + HOVER_GAP;

/** Distance between neighbouring resting centres, tightening with the count. */
function fanSpread(count: number): number {
  if (count <= 1) return 0;
  const want = (FAN_MAX_WIDTH - CARD_W) / (count - 1);
  return Math.min(MAX_SPREAD, Math.max(MIN_SPREAD, want));
}

/** Degrees between neighbours, tightening the same way. */
function fanStepDeg(count: number): number {
  if (count <= 1) return 0;
  return Math.min(STEP_DEG, SPAN_DEG / (count - 1));
}

/** How far the card `distance` along from the one being read is pushed aside. */
function parting(distance: number): number {
  if (distance === 0) return 0;
  const away = PART / 2 ** (Math.abs(distance) - 1);
  return distance < 0 ? -away : away;
}

export interface CardPose {
  /** Where the card rests, from the fan's middle. The seat is measured off it. */
  centre: number;
  /** Degrees, clockwise. Zero for the card under the pointer. */
  rotate: number;
  /** The translate the drawing takes, from the fan's middle. */
  x: number;
  /** How far off the resting line, px up. */
  lift: number;
  scale: number;
  /** Paint order within the fan. The hovered card is always on top. */
  z: number;
}

/**
 * Where every card is drawn.
 *
 * `hovered` is an index, not an instance: this is geometry and knows nothing
 * about the deck. `handHovered` is the whole zone's state — the mock's
 * `#hand:hover .card`, which lifts the cards the pointer is *not* on as well,
 * so the hand opens as one object rather than one card poking out of a row.
 *
 * **The lit card never moves sideways.** It straightens, grows and rises on
 * its own resting centre, so it stays under the pointer that lit it; what
 * makes room for it is the *neighbours* parting. A card that slid as it lifted
 * was the U7 complaint — at the ends of the fan it walked out from under the
 * cursor entirely.
 *
 * `x` is the translate, which is not the centre: the pivot is `FAN_RADIUS`
 * below the card, so a rotation of `r` already carries the card
 * `FAN_RADIUS·sin(r)` sideways. The layout is defined on the centres and the
 * swing is subtracted here, or the spacing would depend on the angle.
 */
export function fanLayout(
  count: number,
  hovered: number | null,
  handHovered: boolean
): CardPose[] {
  const middle = (count - 1) / 2;
  const spread = fanSpread(count);
  const deg = fanStepDeg(count);
  const poses: CardPose[] = [];
  for (let i = 0; i < count; i++) {
    const step = i - middle;
    const lit = hovered === i;
    // The hovered card straightens up: it is being read, and a card being read
    // should not be at an angle.
    const rotate = lit ? 0 : deg * step;
    const centre = spread * step;
    const part = hovered === null ? 0 : parting(i - hovered);
    poses.push({
      centre,
      rotate,
      x: centre + part - FAN_RADIUS * Math.sin((rotate * Math.PI) / 180),
      lift: lit ? CARD_LIFT : handHovered ? HAND_LIFT : 0,
      scale: lit ? HOVER_SCALE : 1,
      // Left under right, so the fan reads as a hand held in one; the lit card
      // takes the top of the pile whatever it was.
      z: lit ? count : i
    });
  }
  return poses;
}

/** The top edge of the drawing, above the resting foot line. */
const drawnTop = (pose: CardPose): number =>
  pose.lift + CARD_H + PIVOT * CARD_H * (pose.scale - 1);

/** A hit area, in fan px: x from the fan's middle, y up from the resting foot. */
export interface SeatBox {
  left: number;
  right: number;
  bottom: number;
  top: number;
}

/**
 * The **seats**: a card's hit area is its *resting* sliver, and a hover never
 * moves one sideways.
 *
 * **A card cannot be its own hit area, and that is not a detail.** A card that
 * rises out from under a stationary pointer stops being under it, so the
 * pointer leaves, so the card drops back under the pointer, so it enters again
 * — a strobe at the frame rate, measured, and it also makes the element
 * permanently "unstable" to Playwright, which waits for two identical bounding
 * boxes before it will click anything. So the seat is a box the layout states
 * and the drawing moves inside it.
 *
 * **The box is the sliver the card rests in.** Cards stack left under right,
 * so a resting card shows the strip between its own left edge and its
 * neighbour's; that strip is the seat, in every state. Slivers are disjoint
 * and contiguous by construction, which is what lets the fan pack to
 * `MIN_SPREAD` without burying the point Playwright hit-tests.
 *
 * **Only the height moves.** `bottom` is the resting foot line always and
 * `top` is the card's own drawn top in the current state, so a hover grows a
 * seat *upward* and never sideways. Two properties, pinned in `hand.test.ts`
 * over every count and every hover:
 *
 * 1. resting seats are disjoint, ordered, and at least `MIN_SPREAD` wide;
 * 2. `seat_i(any state) ⊆ seat_i(lit = i)` — the x is the same box and the lit
 *    top is the highest one, so a card lighting can never drop the pointer
 *    that lit it.
 *
 * **Sifting the hand has to be smooth in both directions, and that is what
 * cut the first version** (designer, second review): a seat that was the
 * card's *visible* region put a 182px lit box on top of the fan, so sweeping
 * right from a lit card crossed six trimmed-away neighbours before anything
 * else lit, while sweeping left worked by luck. Smooth sifting outranks
 * "the hit area follows the eye": the drawing now overhangs its sliver on both
 * sides and takes no pointer, so the pointer just right of a lit card's centre
 * lights the *next* card. That is the sift, and it is deliberate.
 */
export function fanSeats(
  count: number,
  hovered: number | null,
  handHovered: boolean
): SeatBox[] {
  const poses = fanLayout(count, hovered, handHovered);
  // Resting centres, never the drawn ones: parting is a fact about the
  // picture, and a seat that parted with it would move under the pointer.
  const lefts = poses.map((pose) => pose.centre - CARD_W / 2);
  return poses.map((pose, i) => ({
    left: lefts[i],
    // The card to the right is painted over this one, so the sliver ends where
    // that one begins; the last card in the hand keeps its whole face.
    right: i + 1 < count ? lefts[i + 1] : lefts[i] + CARD_W,
    bottom: 0,
    top: drawnTop(pose)
  }));
}

/**
 * How wide the fan reaches, so the zone can be that wide and no wider.
 *
 * The resting span plus a card. Parting pushes a neighbour `PART` past that
 * for as long as the pointer is in the hand, which is overflow the zone does
 * not have to own: nothing is clipped and the inset it registers is a depth.
 */
export function fanWidth(count: number): number {
  if (count <= 0) return 0;
  return fanSpread(count) * (count - 1) + CARD_W;
}
